import { ApiError } from '@dataroom/shared';
import type {
  ChecklistWorkbookProblem,
  InstantiateChecklistRequest,
  InstantiateChecklistXlsxRequest,
  SavedChecklistTemplate,
} from '@dataroom/shared';

/** The blank Excel checklist template, served as a static file from the SPA's own
 * origin (web/admin/public/templates — the Pages worker only proxies /api/*). */
export const BLANK_TEMPLATE_URL = `${import.meta.env.BASE_URL}templates/DataRoom-DD-Checklist-Template.xlsx`;
export const BLANK_TEMPLATE_FILENAME = 'ClosingVault-DD-Checklist-Template.xlsx';

/** The built-in ClosingVault checklist rendered in the upload format (tenant admin
 * on Plus or Professional only — ADR-0044). */
export const DEFAULT_CHECKLIST_XLSX_PATH = '/dd-templates/default/xlsx';
export const DEFAULT_CHECKLIST_FILENAME = 'ClosingVault-DD-Checklist.xlsx';

/** The SAMPLE layout (ADR-0044) — the standard checklist as a spreadsheet, the same
 * list a trial or Starter room is seeded with. Any tenant admin may download it. */
export const SAMPLE_CHECKLIST_XLSX_PATH = '/dd-templates/sample/xlsx';
export const SAMPLE_CHECKLIST_FILENAME = 'ClosingVault-Sample-Checklist.xlsx';

/** The free-trial copy (ADR-0044), shared by the banner, the walkthrough and the
 * checklist dialog so the promise reads the same everywhere. */
export const TRIAL_CHECKLIST_MESSAGE =
  '30 days free with our standard checklist to build on, or your own; the ClosingVault checklist comes with Plus and Professional.';

/** Shown on the locked ClosingVault-checklist option of a free-trial or Starter tenant. */
export const PLUS_PLANS_BADGE = 'Included with Plus and Professional';

/** Shown on the disabled "save this checklist" affordances of a plan that cannot save one. */
export const PROFESSIONAL_ONLY_HINT = 'Included with Professional';

/** True when the plan cannot SAVE a checklist for reuse (a free trial, Starter or Plus —
 * Professional only). An unknown plan (still loading, or unreadable) is not locked — the
 * server decides. */
export function savedChecklistsLocked(
  plan: { includesSavedChecklists?: boolean } | undefined,
): boolean {
  return plan?.includesSavedChecklists === false;
}

/** True when the tenant's plan does NOT include the ClosingVault checklist (a free trial
 * or Starter). An unknown plan (still loading, or unreadable) is not locked — the server
 * decides. */
export function closingVaultLocked(plan: { includesClosingVaultChecklist?: boolean } | undefined): boolean {
  return plan?.includesClosingVaultChecklist === false;
}

/** True when an error is the server's 403 `upgrade_required` (a Plus-and-Professional
 * feature asked for on a free trial or Starter). */
export function isUpgradeRequired(error: unknown): boolean {
  return (
    error instanceof ApiError && error.status === 403 && error.problem?.code === 'upgrade_required'
  );
}

/** Client-side mirror of the server's 5 MB workbook cap, so an oversized file is
 * refused up-front with a clear message instead of a base64 round-trip to a 400. */
export const MAX_CHECKLIST_BYTES = 5 * 1024 * 1024;

/** True when an error is a 403 — the signal that the caller is not a tenant admin
 * (there is no client-side role context), so saved-checklist affordances hide. */
export function isForbidden(error: unknown): boolean {
  return error instanceof ApiError && error.status === 403;
}

/** True when any of the errors is a 401 (session gone → back to /login). */
export function isUnauthorized(...errors: unknown[]): boolean {
  return errors.some((e) => e instanceof ApiError && e.status === 401);
}

export interface ChecklistProblems {
  problems: ChecklistWorkbookProblem[];
  /** Problems beyond the ones listed (the server reports at most 20). */
  more: number;
}

/** The row-level problems of a 422 `invalid_checklist` upload, or null for any
 * other error (which renders as its plain message instead). */
export function checklistProblemsOf(error: unknown): ChecklistProblems | null {
  if (!(error instanceof ApiError) || error.status !== 422) return null;
  const problem = error.problem;
  if (!problem || problem.code !== 'invalid_checklist' || !Array.isArray(problem.problems)) {
    return null;
  }
  const total = problem.totalProblems ?? problem.problems.length;
  return { problems: problem.problems, more: Math.max(total - problem.problems.length, 0) };
}

/** "5 phases · 464 document requests" — the counts line for a checklist. */
export function checklistCountsLabel(counts: {
  phaseCount?: number;
  requestCount?: number;
  categoryCount?: number;
  questionCount?: number;
}): string {
  const parts: string[] = [];
  const add = (n: number | undefined, one: string, many: string) => {
    if (n !== undefined) parts.push(`${n} ${n === 1 ? one : many}`);
  };
  add(counts.phaseCount, 'phase', 'phases');
  add(counts.categoryCount, 'category', 'categories');
  add(counts.requestCount, 'document request', 'document requests');
  add(counts.questionCount, 'question', 'questions');
  return parts.join(' · ');
}

/** A sensible default "save as" name from an uploaded filename (extension off). */
export function nameFromFilename(filename: string): string {
  return filename.replace(/\.[^.]+$/, '').trim();
}

/** Summary line for a saved checklist row / option. */
export function savedChecklistLabel(t: SavedChecklistTemplate): string {
  return `${t.name} (${checklistCountsLabel({ phaseCount: t.phaseCount, requestCount: t.requestCount })})`;
}

/**
 * Download a binary attachment from the API (e.g. an .xlsx render). Uses the raw
 * credentialed `fetch` (the JSON client can't carry bytes), then hands the blob to
 * the browser as a named download. Throws an ApiError with the problem's message
 * on a non-2xx so callers render it like any other failure.
 */
export async function downloadFromApi(path: string, filename: string): Promise<void> {
  const res = await fetch(`/api/v1${path}`, { credentials: 'include' });
  if (!res.ok) {
    let message = res.statusText || 'Download failed.';
    try {
      const body = (await res.json()) as { detail?: string; title?: string };
      message = body.detail ?? body.title ?? message;
    } catch {
      // Non-JSON error body — keep the status text.
    }
    throw new ApiError(res.status, message);
  }
  const blob = await res.blob();
  const url = URL.createObjectURL(blob);
  try {
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    a.remove();
  } finally {
    // Revoke on the next tick so the click has started the download.
    setTimeout(() => URL.revokeObjectURL(url), 0);
  }
}

/** The sources offered by the checklist-setup dialog (`default` — the ClosingVault
 * checklist — is locked on a free trial and on Starter). */
export type ChecklistSource = 'default' | 'standard' | 'upload' | 'saved' | 'skip';

/** What submitting the checklist-setup dialog should do, decided purely from its
 * inputs (so each choice's endpoint/body is unit-testable without a DOM). */
export type ChecklistSetupPlan =
  | { kind: 'close' }
  | { kind: 'invalid'; message: string }
  | { kind: 'instantiate'; body: InstantiateChecklistRequest }
  | { kind: 'upload'; file: File; saveAs?: string };

export function planChecklistSetup(input: {
  source: ChecklistSource;
  file: File | null;
  savedTemplateId: string;
  saveForReuse: boolean;
  saveName: string;
  /** Saving needs the tenant-admin capability; without it `saveAs` is never sent. */
  tenantAdmin: boolean;
  /** A free-trial or Starter tenant cannot use the ClosingVault checklist (ADR-0044; the
   * server refuses it with 403 too — this only avoids the round trip). */
  locked?: boolean;
}): ChecklistSetupPlan {
  switch (input.source) {
    case 'skip':
      return { kind: 'close' };
    case 'default':
      if (input.locked) {
        return {
          kind: 'invalid',
          message: `The ClosingVault checklist is ${PLUS_PLANS_BADGE.toLowerCase()}. Use the standard checklist or upload your own instead.`,
        };
      }
      return { kind: 'instantiate', body: { source: 'default' } };
    case 'standard':
      return { kind: 'instantiate', body: { source: 'standard' } };
    case 'saved':
      return input.savedTemplateId
        ? { kind: 'instantiate', body: { source: 'saved', templateId: input.savedTemplateId } }
        : { kind: 'invalid', message: 'Choose a saved checklist.' };
    case 'upload': {
      if (!input.file)
        return { kind: 'invalid', message: 'Choose your checklist file (.xlsx) first.' };
      if (input.file.size > MAX_CHECKLIST_BYTES) {
        return {
          kind: 'invalid',
          message: 'That file is larger than 5 MB — checklists are capped at 5 MB.',
        };
      }
      if (!input.tenantAdmin || !input.saveForReuse) return { kind: 'upload', file: input.file };
      const saveAs = input.saveName.trim();
      if (!saveAs) {
        return {
          kind: 'invalid',
          message: 'Give the saved checklist a name, or untick “Save this checklist”.',
        };
      }
      return { kind: 'upload', file: input.file, saveAs };
    }
  }
}

/** The instantiate-xlsx body for an encoded upload (`saveAs` only when saving). */
export function checklistXlsxBody(
  file: File,
  contentBase64: string,
  saveAs?: string,
): InstantiateChecklistXlsxRequest {
  return {
    filename: file.name,
    contentType: file.type || 'application/octet-stream',
    contentBase64,
    ...(saveAs ? { saveAs } : {}),
  };
}
